import { useState } from 'react';
import { ReactComponent as MerchantPinEmpty } from 'assets/MerchantPinEmpty.svg';
import Button from 'components/Buttons/Button';
import ModalComponent from 'components/Common/Modal';
import SetPinModal from 'components/MerchantPin/SetPinModal';
import ConfirmationModal from 'components/Common/ConfirmationModal';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useAppSelector } from 'hooks/useRedux';
import { RootState } from 'store';
import {
  useGetEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
} from 'store/apis/employeeApi';
import { Loader } from 'components/Common/Loader';
import toast from 'react-hot-toast';
import { PiPencilSimple, PiTrash } from 'react-icons/pi';

const MerchantPin = () => {
  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const business: BusinessType = businessState?.business || selectedBusiness || null;

  const [showAddPinModal, setShowAddPinModal] = useState(false);
  const [showEditPinModal, setShowEditPinModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);

  const {
    data: employeesData,
    isLoading: isLoadingEmployees,
    refetch,
  } = useGetEmployeesQuery({ businessUuid: business?.uuid || '' }, { skip: !business?.uuid });

  const [createEmployee, { isLoading: isCreating }] = useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateEmployeeMutation();
  const [deleteEmployee, { isLoading: isDeleting }] = useDeleteEmployeeMutation();

  const getEmployeesList = (): EmployeeData[] => {
    if (!employeesData) return [];
    if (Array.isArray(employeesData)) return employeesData;
    return employeesData.data || employeesData.results || employeesData.employees || [];
  };

  const employees = getEmployeesList();

  const formatPin = (pin: string | undefined | null): string => {
    if (!pin) return '--- ---';
    if (pin.length === 6) {
      return `${pin.slice(0, 3)} ${pin.slice(3)}`;
    }
    return pin;
  };

  const parseApiError = (error: unknown): string => {
    const err = error as { data?: Record<string, unknown> };
    const errorData = err?.data;

    if (!errorData) return 'Something went wrong';

    // Check for simple message format: { message: "..." }
    if (typeof errorData.message === 'string') {
      return errorData.message;
    }

    // Check for field validation errors: { passcode: ["..."], name: ["..."] }
    const fieldErrors: string[] = [];
    Object.entries(errorData).forEach(([, value]) => {
      if (Array.isArray(value)) {
        fieldErrors.push(...value.map(String));
      }
    });

    if (fieldErrors.length > 0) {
      return fieldErrors.join('. ');
    }

    return 'Something went wrong';
  };

  const handleAddPin = async (data: { name?: string; pin: string }) => {
    if (!business?.uuid) {
      toast.error('Business not found');
      return;
    }

    try {
      await createEmployee({
        businessUuid: business.uuid,
        ...(data.name && { name: data.name }),
        passcode: data.pin,
      }).unwrap();

      toast.success('Pin created successfully');
      setShowAddPinModal(false);
      refetch();
    } catch (error: unknown) {
      toast.error(parseApiError(error));
    }
  };

  const handleEditPin = async (data: { name?: string; pin: string }) => {
    if (!business?.uuid || !selectedEmployee?.uuid) {
      toast.error('Business or employee not found');
      return;
    }

    try {
      await updateEmployee({
        businessUuid: business.uuid,
        employeeUuid: selectedEmployee.uuid,
        ...(data.name && { name: data.name }),
        passcode: data.pin,
      }).unwrap();

      toast.success('Pin updated successfully');
      setShowEditPinModal(false);
      setSelectedEmployee(null);
      refetch();
    } catch (error: unknown) {
      toast.error(parseApiError(error));
    }
  };

  const handleDeletePin = async () => {
    const employeeUuid = selectedEmployee?.uuid || selectedEmployee?.id;

    if (!business?.uuid || !employeeUuid) {
      toast.error('Business or employee not found');
      return;
    }

    try {
      await deleteEmployee({
        businessUuid: business.uuid,
        employeeUuid,
      }).unwrap();

      toast.success('Pin deleted successfully');
      setShowDeleteModal(false);
      setSelectedEmployee(null);
      await refetch();
    } catch (error: unknown) {
      toast.error(parseApiError(error));
    }
  };

  const openEditModal = (employee: EmployeeData) => {
    setSelectedEmployee(employee);
    setShowEditPinModal(true);
  };

  const openDeleteModal = (employee: EmployeeData) => {
    setSelectedEmployee(employee);
    setShowDeleteModal(true);
  };

  const hasPins = employees.length > 0;

  if (isLoadingEmployees) {
    return (
      <div className="bg-white flex items-center justify-center py-20">
        <Loader size={8} />
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="px-8 pt-6 pb-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h1 className="font-poppins font-medium text-[24px] leading-[100%] tracking-[0.02em] text-[#111653]">
            Merchant App PIN
          </h1>
          <span className="font-poppins font-normal text-sm text-gray-500">
            A secure 6-digit code used by merchants to log in and approve transactions in the
            Merchant App.
          </span>
        </div>
      </div>

      <div className="px-8 pb-8">
        <div className="w-full max-w-[1134px] h-[513px] border border-gray200 rounded-lg bg-white">
          <div className="h-[48px] md:h-[59px] rounded-t-lg px-4 md:px-5 py-3 md:py-4 border-b border-gray200">
            <h2 className="font-poppins font-medium text-[16px] leading-[100%] tracking-[0%] text-darkBlack align-middle">
              PIN's
            </h2>
          </div>

          {hasPins ? (
            <div className="flex flex-col h-[calc(513px-59px)]">
              {/* Pins list with scroll */}
              <div className="flex-1 overflow-y-auto">
                {employees.map((employee, index) => {
                  const pinValue = employee.passcode || employee.pin || employee.code || '';
                  return (
                    <div
                      key={employee.uuid || employee.id || index}
                      className={`flex items-center justify-between px-4 md:px-5 py-4 ${
                        index !== employees.length - 1 ? 'border-b border-gray200' : ''
                      }`}
                    >
                      <span className="font-poppins font-normal text-[14px] leading-[100%] tracking-[0%] text-[#0B1215]">
                        {employee.name || ''}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="font-poppins font-medium text-[16px] leading-[100%] tracking-[0%] text-[#4C2A69]">
                          {formatPin(pinValue)}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEditModal(employee)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <PiPencilSimple className="w-5 h-5 text-[#0B1215]" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteModal(employee)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <PiTrash className="w-5 h-5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add pin button - always at bottom */}
              <div className="flex justify-end px-4 md:px-5 py-4 border-t border-gray200">
                <Button
                  label="+ Add PIN"
                  onClick={() => setShowAddPinModal(true)}
                  customClass="!w-[109px] !h-[33px] !py-[6px] !px-[20px] !mb-0 !rounded-[40px] !gap-2"
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[calc(513px-59px)] px-4 md:px-8">
              <div className="mb-4">
                <MerchantPinEmpty className="w-[280px] h-[280px]" />
              </div>

              <p className="font-poppins font-normal text-[16px] leading-[150%] tracking-[0%] text-center text-darkBlue max-w-[1054px] mb-2">
                You have not added any pins for cashiers to log into the merchant app and scan for
                subscriptions.
              </p>
              <p className="text-sm md:text-base text-darkBlue text-center max-w-full md:max-w-2xl px-2 mb-4 font-poppins leading-[150%]">
                Click on "<span className="text-primary font-semibold">+ Add PIN</span>" button to
                add a pin for cashier.
              </p>

              <Button
                label="+ Add PIN"
                onClick={() => setShowAddPinModal(true)}
                customClass="!w-[109px] !h-[33px] !py-[6px] !px-[20px] !mb-0 !rounded-[40px] !gap-2 !font-poppins !font-medium !text-[14px] !leading-[100%] !tracking-[0%]"
              />
            </div>
          )}
        </div>
      </div>

      {/* Add Pin Modal */}
      {showAddPinModal && (
        <ModalComponent
          onClose={() => setShowAddPinModal(false)}
          containerClass="!w-[560px] !h-auto !p-0 !rounded-[8px] !overflow-hidden"
          allowBodyScroll={false}
        >
          <SetPinModal
            onClose={() => setShowAddPinModal(false)}
            onSubmit={handleAddPin}
            isLoading={isCreating}
          />
        </ModalComponent>
      )}

      {/* Edit Pin Modal */}
      {showEditPinModal && selectedEmployee && (
        <ModalComponent
          onClose={() => {
            setShowEditPinModal(false);
            setSelectedEmployee(null);
          }}
          containerClass="!w-[560px] !h-auto !p-0 !rounded-[8px] !overflow-hidden"
          allowBodyScroll={false}
        >
          <SetPinModal
            onClose={() => {
              setShowEditPinModal(false);
              setSelectedEmployee(null);
            }}
            onSubmit={handleEditPin}
            isLoading={isUpdating}
            isEditMode
            initialData={{
              name: selectedEmployee.name || '',
              pin: selectedEmployee.passcode || selectedEmployee.pin || selectedEmployee.code || '',
            }}
          />
        </ModalComponent>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal && !!selectedEmployee}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedEmployee(null);
        }}
        onConfirmClick={handleDeletePin}
        renderConfirmationMessage={
          <div className="text-center">
            <h3 className="font-poppins font-semibold text-[18px] text-darkBlue mb-2">
              Delete Pin
            </h3>
            <p className="font-poppins text-[14px] text-darkBlue">
              Are you sure you want to delete this pin? This action cannot be undone.
            </p>
          </div>
        }
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
        loading={isDeleting}
      />
    </div>
  );
};

export default MerchantPin;
